import { useMemo } from 'react';
import type { ColumnType } from '../components/datatable/utils/type';
import { getColumnText } from '../components/datatable/utils/text';

export function useFiltering<T>(
  rows: T[],
  columns: ColumnType<T>[],
  term: string,
  columnIndex?: number,
  columnFilters: string[] = [],
  coincidence = false,
  selectedFilter?: string,
  columnFilter?: number,
  visibleColumnIndexes?: number[]
) {
  return useMemo(() => {
    if (!term && !columnFilters.some(Boolean) && !selectedFilter) {
      return rows;
    }

    const searchableColumnIndexes =
      visibleColumnIndexes !== undefined ? visibleColumnIndexes : columns.map((_, index) => index);

    const visibleColumnsSet = new Set(searchableColumnIndexes);

    return rows.filter((row) => {
      /* =========================
       * SEARCH GLOBAL
       * ========================= */
      if (term) {
        const indexes =
          columnIndex !== undefined
            ? visibleColumnsSet.has(columnIndex)
              ? [columnIndex]
              : []
            : searchableColumnIndexes;

        const text = indexes.map((index) => getColumnText(columns[index], row)).join(' ');

        const a = text.toLowerCase();
        const b = term.toLowerCase();

        const match = coincidence ? a === b : a.includes(b);
        if (!match) return false;
      }

      /* =========================
       * FILTERS BY COLUMN (inputs)
       * ========================= */
      for (let i = 0; i < columnFilters.length; i++) {
        const f = columnFilters[i];
        if (!f) continue;
        if (!visibleColumnsSet.has(i)) continue;

        const v = getColumnText(columns[i], row);

        if (!v.toLowerCase().includes(f.toLowerCase())) {
          return false;
        }
      }

      /* =========================
       * FILTER GENERAL (select)
       * ========================= */
      if (selectedFilter) {
        const value = selectedFilter.toLowerCase();

        // 👉 Filtrar por columna específica
        if (columnFilter !== undefined && columns[columnFilter]) {
          const v = getColumnText(columns[columnFilter], row);

          if (!v.toLowerCase().includes(value)) {
            return false;
          }
        } else {
          // 👉 Fallback: buscar en todas las columnas
          const allText = searchableColumnIndexes
            .map((index) => getColumnText(columns[index], row))
            .join(' ')
            .toLowerCase();

          if (!allText.includes(value)) {
            return false;
          }
        }
      }

      return true;
    });
  }, [
    rows,
    columns,
    term,
    columnIndex,
    columnFilters,
    coincidence,
    selectedFilter,
    columnFilter,
    visibleColumnIndexes
  ]);
}
